import { ModuleEntry } from './module-access.logic';
import { isTodosVisibilityItem, unreadMessageBadgeCount } from '../util/portal-chat-unread-badge';

/** Etapa de un flujo, tal como la devuelve `getVisibility`. */
export interface VisibilidadItem {
  id?: number | string;
  name?: string;
  count?: number;
  /** Si es true, el contador suma al badge del flujo (misma regla que el sidebar de Flows). */
  allowCount?: boolean;
  /** La etapa que el menú abre al pulsar el flujo. En juicios es "Todos". */
  defaultview?: boolean;
  /** Mensajes del cliente (portal) sin leer en la etapa. */
  unreadClientMessageCount?: number;
}

/** Grupo de visibilidad: un flujo del tenant. */
export interface GrupoVisibilidad {
  id?: number | string;
  name?: string;
  /** Etiqueta de la Suite. Vacío o ausente: la Suite usa `name`. La vista clásica ignora este campo. */
  alias?: string | null;
  /** Sección de la suite: `juicios`, `oficios`, `clm`, `solicitudes`, o null si no entra en el menú. */
  suitemodule?: string | null;
  /** Mensajes del cliente (portal) sin leer en el flujo. */
  unreadClientMessageCount?: number;
  items?: VisibilidadItem[];
}

/** Texto del flujo en la Suite: alias si tiene contenido, si no el name. */
function etiquetaSuite(grupo: GrupoVisibilidad): string {
  const alias = (grupo.alias ?? '').trim();
  return alias || grupo.name || '';
}

/** Sección del sidebar a la que pertenece el flujo, según `suitemodule`. */
export function seccionDeFlujo(
  grupo: GrupoVisibilidad | null | undefined
): 'juicio' | 'oficio' | 'contrato' | 'solicitud' | 'otro' {
  if (grupo?.suitemodule === 'juicios') return 'juicio';
  if (grupo?.suitemodule === 'oficios') return 'oficio';
  if (grupo?.suitemodule === 'clm') return 'contrato';
  if (grupo?.suitemodule === 'solicitudes') return 'solicitud';
  return 'otro';
}

/**
 * Flujos de una sección de la suite (juicio / contrato / oficio / solicitud)
 * con sus visibilidades debajo. Misma forma: badge, link a etapa inicial, children.
 */
export function flujosDeSeccion(
  grupos: GrupoVisibilidad[] | null | undefined,
  seccion: 'juicio' | 'oficio' | 'contrato' | 'solicitud'
): ModuleEntry[] {
  if (!Array.isArray(grupos)) return [];

  return grupos
    .filter((g) => g?.name && g.name !== 'Pendientes' && g.id != null && seccionDeFlujo(g) === seccion)
    .map((g) => {
      const items = Array.isArray(g.items) ? g.items : [];
      const children: ModuleEntry[] = items
        .filter((item) => item?.name && item.id != null)
        .map((item) => ({
          name: `flow-visibility-${g.id}-${item.id}`,
          alias: item.name,
          link: `/flows/requirements/g/${g.id}/v/${item.id}`,
          visible: true,
          disabled: false,
          count: item.count ?? 0,
          unreadMessages: isTodosVisibilityItem(item) ? 0 : unreadMessageBadgeCount(item.unreadClientMessageCount),
        }));
      const inicial = etapaInicial(items);
      const linkInicial = inicial
        ? `/flows/requirements/g/${g.id}/v/${inicial.id}`
        : '/flows/requirements';
      return {
        name: `flow-group-${g.id}`,
        alias: etiquetaSuite(g),
        link: linkInicial,
        visible: true,
        disabled: false,
        permissions: ['flows.admin'],
        count: badgeDeFlujo(items),
        unreadMessages: unreadMessageBadgeCount(g.unreadClientMessageCount),
        children,
      };
    });
}

/** Flujos de juicio para GESTIÓN JUICIOS. */
export function flujosDeJuicios(grupos: GrupoVisibilidad[] | null | undefined): ModuleEntry[] {
  return flujosDeSeccion(grupos, 'juicio');
}

/**
 * Hijos de Gestión Juicios: Todos los juicios, los flujos del tenant y después
 * el resto del catálogo (Calendario Audiencias, Config PJUD).
 * Sin flujos de juicio, Todos los juicios no tiene nada que juntar y no va.
 * `totalTodos` es su badge: el total de la grilla, no la suma de los flujos.
 * Su badge verde de mensajes sí es la suma de los flujos de juicio.
 */
export function hijosDeJuicios<T extends { name?: string; count?: number; unreadMessages?: number }>(
  flujos: T[] | null | undefined,
  catalogo: T[] | null | undefined,
  totalTodos: number | null = null,
): T[] {
  const lista = flujos ?? [];
  const mensajes = lista.reduce((total, f) => total + unreadMessageBadgeCount(f.unreadMessages), 0);
  const todos = (catalogo ?? [])
    .filter((h) => h.name === 'juicios-todos')
    .map((h) => ({ ...h, unreadMessages: mensajes, ...(totalTodos == null ? {} : { count: totalTodos }) }));
  const resto = (catalogo ?? []).filter((h) => h.name !== 'juicios-todos');
  return lista.length ? [...todos, ...lista, ...resto] : resto;
}

/** Flujos de contratos (CLM) con etapas, igual que juicios. */
export function flujosDeContratos(grupos: GrupoVisibilidad[] | null | undefined): ModuleEntry[] {
  return flujosDeSeccion(grupos, 'contrato');
}

/** Flujos de oficios / reclamos con etapas, igual que juicios. Tareas no entra acá. */
export function flujosDeOficios(grupos: GrupoVisibilidad[] | null | undefined): ModuleEntry[] {
  return flujosDeSeccion(grupos, 'oficio');
}

/** Flujo de tareas para SOLICITUDES LEGALES. */
export function flujosDeSolicitudes(grupos: GrupoVisibilidad[] | null | undefined): ModuleEntry[] {
  return flujosDeSeccion(grupos, 'solicitud');
}

/**
 * Etapa que abre el menú al pulsar el flujo: la marcada defaultview, si no
 * la que se llama "Todos", si no la primera. Igual que el sidebar de Flows,
 * donde orderindex 0 y defaultview es Todos.
 */
function etapaInicial(items: VisibilidadItem[]): VisibilidadItem | undefined {
  const validos = items.filter((item) => item?.name && item.id != null);
  return validos.find((item) => item.defaultview)
    || validos.find((item) => /^todos$/i.test(item.name ?? ''))
    || validos[0];
}

/** Suma de las etapas que el backend marca como contables. */
function badgeDeFlujo(items: VisibilidadItem[]): number {
  return items.reduce(
    (total, item) => (item?.allowCount ? total + (item.count ?? 0) : total),
    0
  );
}

/** Parámetros de la query, sin el `?`. */
function queryDe(ruta: string | null | undefined): Array<[string, string]> {
  const q = String(ruta || '').split('?')[1]?.split('#')[0];
  if (!q) return [];
  return q.split('&').filter(Boolean).map((par) => {
    const [k, v = ''] = par.split('=');
    return [k, v] as [string, string];
  });
}

/**
 * La URL es ese link, o una ruta debajo.
 * Si el link trae query (p. ej. `/clm-sign?tab=firmas`), solo cuenta esa
 * página: no es prefijo de `/clm-sign/templates`.
 */
export function linkCoincideConUrl(url: string, link: string | null | undefined): boolean {
  const path = (url || '').split(/[?#]/)[0];
  const propio = (link || '').split(/[?#]/)[0];
  if (!path || !propio) return false;
  const mismoPath = path === propio;
  const debajo = path.startsWith(`${propio}/`);
  if (!mismoPath && !debajo) return false;

  const declarada = queryDe(link);
  if (!declarada.length) return true;
  if (!mismoPath) return false;
  const actuales = new Map(queryDe(url));
  return declarada.every(([k, v]) => actuales.get(k) === v);
}

/**
 * Otro hijo del menú coincide con la URL con un link que cuelga del de este
 * (Config PJUD `/admin/configuracion-pjud` frente a Mantenedores `/admin`): gana el más específico.
 */
export function hayHijoMasEspecifico(url: string, link: string | null | undefined, otros: Array<{ link?: string }>): boolean {
  const propio = (link || '').split(/[?#]/)[0];
  if (!propio) return false;
  return otros.some((otro) => {
    const suyo = (otro.link || '').split(/[?#]/)[0];
    return suyo.startsWith(`${propio}/`) && linkCoincideConUrl(url, otro.link);
  });
}

/**
 * Si la URL cae bajo un flujo del sidebar (flow-group-ID) o bajo su link/etapas.
 * Para CLM/juicios/oficios: cualquier `/flows/requirements/g/{id}/…` cuenta,
 * no solo la etapa inicial del link del padre.
 */
export function urlPerteneceAHijo(
  url: string,
  child: { name?: string; link?: string; children?: Array<{ link?: string }> | null }
): boolean {
  const path = (url || '').split('?')[0];
  if (!path) return false;

  const groupMatch = /^flow-group-(.+)$/.exec(child.name ?? '');
  if (groupMatch && linkCoincideConUrl(path, `/flows/requirements/g/${groupMatch[1]}`)) return true;

  if (linkCoincideConUrl(url, child.link)) return true;

  return !!child.children?.some((gc) => linkCoincideConUrl(url, gc.link));
}

/** A dónde cae el clic en un padre de la suite. */
export interface DestinoPadre {
  /** Página del padre, o la etapa inicial del primer flujo. null si no hay a dónde ir. */
  url: string | null;
  /** Flujo que queda abierto en el menú. null cuando el padre abre su propia página. */
  flujo: string | null;
}

/** Qué agrupadores siguen desplegados después de un clic en el menú. */
export interface EstadoMenu {
  modulo: string | null;
  flujo: string | null;
}

/**
 * Un clic deja abierto solo el módulo de esa opción.
 * El flujo queda abierto solo si el clic fue ese flujo o una etapa suya.
 * Un padre sin hijos no deja ningún agrupador abierto, ni dentro ni fuera.
 */
export function menuAlPulsar(clic: {
  modulo: string;
  flujo: string | null;
  dejaElModuloAbierto: boolean;
  cierraElFlujo: boolean;
}): EstadoMenu {
  if (!clic.dejaElModuloAbierto) return { modulo: null, flujo: null };
  if (clic.cierraElFlujo) return { modulo: clic.modulo, flujo: null };
  return { modulo: clic.modulo, flujo: clic.flujo };
}

/**
 * Si la URL cae en un flujo o en una etapa, ese flujo sigue abierto.
 * Una hoja (Calendario Audiencias, Firma, Mantenedores) no deja ningún flujo abierto.
 */
export function flujoQueQuedaAbierto(
  child: { name?: string; children?: Array<{ link?: string }> | null },
  url: string,
): string | null {
  const esFlujo = /^flow-group-/.test(child.name || '') && !!child.children?.length;
  const enEtapa = !!child.children?.some((etapa) => linkCoincideConUrl(url, etapa.link));
  if (enEtapa || esFlujo) return child.name ?? null;
  return null;
}

export interface ClicEnPadre {
  abre: boolean;
  navega: boolean;
}

/**
 * Cada clic en el padre abre o cierra sus hijos. Si tiene página, además va a
 * ella siempre. Si no tiene, abrir cae en el primer flujo y cerrar no navega.
 */
export function clicEnPadre(link: string | null | undefined, yaAbierto: boolean): ClicEnPadre {
  const conPagina = !!(link ?? '').trim();
  return { abre: !yaAbierto, navega: conPagina || !yaAbierto };
}

/**
 * Un padre que el usuario cerró no se reabre solo mientras la URL sea su
 * propia página. Cualquier otra navegación suelta el cierre.
 */
export function cierreSigueVigente(link: string | null | undefined, url: string): boolean {
  const pagina = (link ?? '').split('?')[0].trim();
  if (!pagina) return false;
  const path = url.split('?')[0];
  return path === pagina || path.startsWith(`${pagina}/`);
}

/**
 * Un padre puede tener página propia (Notificaciones, Calendario, Indicadores
 * PJUD…). Si la tiene y el perfil puede verla, el clic va ahí. Si no, cae en
 * el primer flujo habilitado y el menú se posiciona en ese flujo.
 */
export function destinoAlAbrirPadre(
  link: string | null | undefined,
  puedeVerPagina: boolean,
  hijos: Array<{ name?: string; link?: string; disabled?: unknown; children?: unknown[] | null }> | null | undefined,
): DestinoPadre {
  const pagina = (link ?? '').trim();
  if (pagina && puedeVerPagina) {
    return { url: pagina, flujo: null };
  }
  if (!Array.isArray(hijos)) return { url: null, flujo: null };
  const habilitados = hijos.filter((hijo) => hijo?.link && !hijo.disabled);
  const flujos = habilitados.filter((hijo) => /^flow-group-/.test(hijo.name || ''));
  const primero = (flujos.length ? flujos : habilitados)[0];
  if (!primero?.link) return { url: null, flujo: null };
  const tieneEtapas = Array.isArray(primero.children) && primero.children.length > 0;
  return {
    url: primero.link,
    flujo: tieneEtapas ? (primero.name ?? null) : null,
  };
}

/**
 * Aterrizaje al entrar a un módulo con hijos (p. ej. desde el Intro):
 * el link del primer hijo habilitado. En juicios ese link ya es la etapa
 * inicial (Todos). Sin hijos, null.
 */
export function linkAterrizajeDesdeHijos(
  hijos: Array<{ link?: string; disabled?: unknown } | null | undefined> | null | undefined
): string | null {
  if (!Array.isArray(hijos)) return null;
  const primero = hijos.find((h) => h?.link && !h.disabled);
  return primero?.link ?? null;
}
